import { Display, toggleClass, toggleVisible } from "@cervval.js/client-html5-core/src/tools/css.tools";
import { getVOFValue } from "@cervval.js/common/src/tools/object.tools";
import { MenuItemDefinition, MenuItemInsertion } from "./menuDefinition";

export const DEFAULT_MENU_FACTORY_UID = "default";
export const DEFAULT_MENU_SECTION = "defaultMenuSection";
/** is used to tag dynamically added element to only remove them when rebuilding the menus */
export const IS_DYNAMIQUE_ATTRIBUTE = "isDynamique";

/**
 * The MenuItemHtmlElements is the result of the factory.newMenuItem
 * It usually returns a 'li' element that contains a 'a' element
 */
export interface MenuItemHtmlElements {
    /** element that was inserted in the dom */
    mainElement: HTMLElement;
    /** link element (inside element) */
    linkElement?: HTMLAnchorElement;
    /** already inserted in DOM by the factory */
    alreadyInserted?: boolean;
}

/**
 * The MenuFactory builds menu items and separators
 * It also applies style of the 'active' items and display of dynamic menus
 */
export interface MenuFactory {
    /**
     * @returns a MenuItem corresponding to the provided item definition
     */
    newMenuItem(definition: MenuItemDefinition, menuUid: string, sectionUid?: string, insertion?: MenuItemInsertion): MenuItemHtmlElements;

    /**
     * @returns a separator
     */
    newSeparator(menuUid: string, sectionUid?: string): HTMLElement;

    /**
     * Add or remove relevant classes so that item appears 'active'
     */
    setActive(elements: MenuItemHtmlElements, active: boolean): void;

    /**
     * Hide or show menu
     */
    setDisplay(menu: HTMLElement, displayed: boolean): void;
}

/**
 * Factories by uid
 */
export type MenuFactories = Record<string, MenuFactory>;

/**
 * Default menu factory that creates <li><a href...> menu items
 */
export class DefaultMenuFactory implements MenuFactory {

    /**
     * @param options
     * @param options.liCssClasses css classes to add to items
     * @param options.liActiveCssClass css class to add to li when item is 'active'
     * @param options.linkCssClasses css classes to add to items
     * @param options.linkActiveCssClass css class to add to li when item is 'active'
     * @param options.linkRef ref of link element, usefull to acces it easily from Ts
     * @param options.linkLabelSpanCssClasses if classes are provided, the label is placed in a span associated to provided classes and no &nbsp; is added between the icon and the label
     * @param options.iconCssClasses css classes to add to icons (not used when item.iconHtml is defined)
     * @param options.separatorCssClasses css classes to add to separators
     * @param options.visibleDisplay [default block] style.display value to set when menu is visible
     * @param options.aOnly if true, no li will be added (only "a" will be append)
     * @param option.elementsToUpdateOnMenuActivation list of DOM element and Css class to add to when item is 'active'
     */
    constructor(public options?: {
        liCssClasses?: string, liActiveCssClass?: string,
        linkCssClasses?: string, linkActiveCssClass?: string, linkRef?: string,
        linkLabelSpanCssClasses?: string, iconCssClasses?: string,
        separatorCssClasses?: string,
        visibleDisplay?: Display, aOnly?: boolean,
        elementsToUpdateOnMenuActivation?: { domElement: HTMLElement, activeCssClass: string }[]
    }) { }

    /**
     * @returns a li MenuItem corresponding to the provided item definition
     */
    public newMenuItem(item: MenuItemDefinition, menuUid: string, sectionUid: string): MenuItemHtmlElements {
        const a = document.createElement("a");
        if (this.options?.linkCssClasses) {
            a.className = this.options.linkCssClasses;
        }
        if (this.options?.linkRef) {
            a.setAttribute("ref", this.options.linkRef);
        }
        let itemHtml = "";
        if (item.iconClasses) {
            const iconClasses = item.iconClasses + (this.options?.iconCssClasses ? " " + this.options?.iconCssClasses : "");
            itemHtml = `<i class="${iconClasses}"></i>`;
        } else if (item.iconHtml) {
            itemHtml = item.iconHtml;
        }
        if ((item.iconClasses || item.iconHtml) && item.label && !this.options?.linkLabelSpanCssClasses) {
            itemHtml += "&nbsp";// if label is not within a span, we add a space between icon and label
        }
        const itemLabel = getVOFValue(item.label);
        if (itemLabel) {
            if (this.options?.linkLabelSpanCssClasses) {
                itemHtml += `<span class="${this.options.linkLabelSpanCssClasses}">${itemLabel}</span>`;
            } else {
                itemHtml += itemLabel;
            }
        }
        a.innerHTML = itemHtml;
        if (item.url) {
            a.href = item.url;
        }

        if (this.options?.aOnly) {
            // no li will be added (only "a" will be append)
            return { mainElement: a, linkElement: a };
        }

        const li = document.createElement("li");
        li.setAttribute(IS_DYNAMIQUE_ATTRIBUTE, "true");
        if (this.options?.liCssClasses) {
            li.className = this.options.liCssClasses;
        }
        li.appendChild(a);
        return { mainElement: li, linkElement: a };
    }

    /**
     * @returns a li separator
     */
    public newSeparator(): HTMLElement {
        const separator = document.createElement("li");
        separator.style.cssText = "height:1px;margin:9px 0;background-color: #e5e5e5;"; // TODO move css to class separator
        separator.innerHTML = "&nbsp;";
        separator.classList.add("separator");
        separator.setAttribute(IS_DYNAMIQUE_ATTRIBUTE, "true");
        return separator;
    }

    /**
     * Add or remove relevant classes so that item appears 'active'
     */
    public setActive(elements: MenuItemHtmlElements, active: boolean): void {
        // Check option
        if (!this.options) { return; }

        if (this.options.liActiveCssClass) {
            toggleClass(elements.mainElement, active, this.options.liActiveCssClass);

        }
        if (this.options.linkActiveCssClass && elements.linkElement) {
            toggleClass(elements.linkElement, active, this.options.linkActiveCssClass);
        }
        if (this.options.elementsToUpdateOnMenuActivation) {
            for (const { domElement, activeCssClass: cssClass } of this.options.elementsToUpdateOnMenuActivation) {
                toggleClass(domElement, active, cssClass);
            }
        }
    }

    /**
     * Hide or show menu element
     */
    public setDisplay(menu: HTMLElement, displayed: boolean): void {
        toggleVisible(menu, displayed, this.options?.visibleDisplay ?? Display.BLOCK);
    }

}